import { textDataAttr } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { createContext, useContext } from "react";

/**
 * The badge's state, provided by {@link SphereAIChatSection} (which owns both the
 * dismissal state and the transport that reads it) and consumed by {@link AppContextChip}
 * rendered deep inside the styled Thread's composer. A React context is the bridge because
 * the Thread's composer slots receive no props.
 */
export type AppContextChipState = {
	/** Short human-readable summary of the attached page context (e.g. `"Portfolio · Benchmark"`), or `null` when there's nothing to attach. */
	label: string | null;
	/** Whether the context is currently attached — i.e. will be sent with the next turn. */
	active: boolean;
	/** Toggle the current context on/off — hides it from the next turn, or shares it again. */
	toggle: () => void;
};

const AppContextChipContext = createContext<AppContextChipState>({
	label: null,
	active: false,
	toggle: () => {},
});

export const AppContextChipProvider = AppContextChipContext.Provider;

/**
 * The "attached context" badge shown above the composer input. It advertises that the
 * chat can see what the user is currently viewing (selected portfolio, outlook filters,
 * …). Clicking it toggles the context off (hidden — suppressed from subsequent turns) or
 * back on; it stays visible in a muted, struck-through state while off so the user can
 * always re-enable it. Renders nothing only when there is no context to share.
 *
 * Registered as the Thread's `ComposerContext` slot, which puts it on the composer's action row,
 * just right of the add-attachment button behind a hairline. Returning `null` with no context is
 * what empties that slot, so the slot's separator goes away with the badge (see ComposerAction) —
 * hence no wrapper element here: an empty div would keep the slot occupied.
 */
export function AppContextChip(): React.JSX.Element | null {
	const { label, active, toggle } = useContext(AppContextChipContext);

	if (!label) return null;

	return (
		<button
			type="button"
			onClick={toggle}
			aria-pressed={active}
			title={
				active
					? "Sphere can see what you're currently viewing. Click to keep it out of this conversation."
					: "Your current page context is hidden from Sphere. Click to share it again."
			}
			className={toClassName({
				// `min-w-0` so a long label truncates instead of pushing the send button off the row.
				"border-border inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors": true,
				"bg-muted text-muted-foreground hover:bg-accent": active,
				// Off state: N400 is the palette's dimmer grey — a real step, rather than a
				// half-opacity wash of the N500 the token carries.
				"text-[var(--palette-N400)] hover:text-muted-foreground border-dashed": !active,
			})}
			{...textDataAttr("Body/S/Book")}
		>
			{/* Tags glyph reads as a status/context indicator (not an "attach file" action). Its fill is
			    currentColor, so it inherits the chip's muted color and dims with it when toggled off. */}
			<TagIcon className="size-3 shrink-0" />
			<span className={toClassName({ truncate: true, "line-through": !active })}>{label}</span>
		</button>
	);
}

function TagIcon({ className }: { className?: string }): React.JSX.Element {
	return (
		<svg viewBox="0 0 24 24" fill="none" className={className} role="img" xmlns="http://www.w3.org/2000/svg">
			<title>Attached context</title>
			<g clipPath="url(#appContextTagClip)">
				<path
					d="M20.2504 7.69995C20.2504 7.00928 19.691 6.45014 19.0004 6.44995H8.1166C7.71308 6.45009 7.33505 6.64511 7.10097 6.97339H7.09999L4.02968 11.2742H4.02871C3.7185 11.7081 3.71863 12.2923 4.02871 12.7263L4.02968 12.7273L7.09999 17.0271C7.33409 17.3548 7.71292 17.5494 8.1166 17.5496H19.0004C19.691 17.5494 20.2504 16.9902 20.2504 16.2996V7.69995ZM21.7504 16.2996C21.7504 17.8187 20.5194 19.0494 19.0004 19.0496H8.1166C7.2286 19.0494 6.39502 18.6212 5.87929 17.8992L2.80996 13.5984L2.80898 13.5994C2.12546 12.6435 2.12559 11.358 2.80898 10.4021L5.87929 6.10229L5.97988 5.97046C6.49966 5.32727 7.284 4.95008 8.1166 4.94995H19.0004C20.5194 4.95014 21.7504 6.18085 21.7504 7.69995V16.2996Z"
					fill="currentColor"
				/>
				<path
					d="M7.49712 10.197C8.43073 9.26335 9.90766 9.20521 10.9092 10.0222L11.1036 10.197L11.2784 10.3903C12.0953 11.3919 12.0372 12.8698 11.1036 13.8034C10.1078 14.7991 8.49298 14.799 7.49712 13.8034C6.50129 12.8076 6.50128 11.1928 7.49712 10.197ZM9.96294 11.1852C9.55053 10.8488 8.94211 10.8731 8.55767 11.2575C8.14769 11.6675 8.14781 12.3318 8.55767 12.7419C8.96771 13.1519 9.63199 13.1519 10.042 12.7419C10.4264 12.3575 10.4506 11.75 10.1143 11.3376L10.042 11.2575L9.96294 11.1852Z"
					fill="currentColor"
				/>
			</g>
			<defs>
				<clipPath id="appContextTagClip">
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</svg>
	);
}
